import { KEYUTIL, KJUR, RSAKey, datetozulu } from "jsrsasign";
import { userInfo, hostname } from 'node:os';
import { randomBytes } from 'node:crypto';

const userAndHost = `${userInfo().username}@${hostname()}`;

function randomSerialNumber(): string {
    const bytes = randomBytes(16);
    return bytes.toString('hex');
}

const DEFAULT_ORGANISATION = `io.Gateway Dev CA`;
export const DEFAULT_CA_NAME = `${DEFAULT_ORGANISATION} ${userAndHost}`;

export function generateRootCA(options?: { name?: string, passphrase?: string } ): { key: string, cert: string } {

    const commonName = options?.name || DEFAULT_CA_NAME;
    const passphrase = options?.passphrase;

    // STEP 1. generate a key pair for Root CA (secp384r1 for higher security)
    const kp = KEYUTIL.generateKeypair("EC", "secp384r1");
    const prv = kp.prvKeyObj;
    const pub = kp.pubKeyObj;

    const prvPem = passphrase
        ? KEYUTIL.getPEM(prv, "PKCS8PRV", passphrase, "AES-256-CBC")
        : KEYUTIL.getPEM(prv, "PKCS8PRV");

    // STEP 2: create self-signed CA certificate
    const cert = new KJUR.asn1.x509.Certificate({
        version: 3,
        serial: { hex: randomSerialNumber() },
        issuer: { str: `/CN=${commonName}/O=${DEFAULT_ORGANISATION}/OU=${userAndHost}` },
        subject: { str: `/CN=${commonName}/O=${DEFAULT_ORGANISATION}/OU=${userAndHost}` },

        notbefore: datetozulu(new Date(Date.now() - 60 * 1000), false, false),
        notafter: datetozulu(new Date(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000), false, false), // 10 years

        sbjpubkey: pub,

        ext: [
            {extname: "basicConstraints", critical: true, cA: true},
            {extname: "keyUsage", critical: true, names: ["keyCertSign"]}
        ],

        sigalg: "SHA384withECDSA",
        cakey: prv
    });

    const certPem = cert.getPEM();

    return {
        key: prvPem,
        cert: certPem
    };
}


/**
 * Generate a certificate with custom SAN entries
 * @param cakey - CA private key
 * @param issuer - Issuer DN string
 * @param sanEntries - Array of SAN entry strings (e.g., "localhost", "IP:192.168.1.1", "EMAIL:user@example.com")
 * @param isClient - Generate client cert (clientAuth) vs server cert (serverAuth)
 * @param validityDays - Certificate validity period
 */
export function generateCert(
    cakey: string | RSAKey | KJUR.crypto.DSA | KJUR.crypto.ECDSA,
    issuer: string,
    sanEntries: string[],
    isClient: boolean = false,
    validityDays: number = 7
): {
    key: string,
    cert: string
} {
    // STEP 1. generate key pair (secp256r1 for faster generation)
    const kp = KEYUTIL.generateKeypair("EC", "secp256r1");
    const prv = kp.prvKeyObj;
    const pub = kp.pubKeyObj;

    const prvPem = KEYUTIL.getPEM(prv, "PKCS8PRV");

    // Parse SAN entries
    const sanArray: Array<{dns?: string, ip?: string, rfc822?: string}> = [];
    let commonName = isClient ? 'dev-user' : 'localhost';

    for (const entry of sanEntries) {
        if (entry.toLowerCase().startsWith('ip:')) {
            sanArray.push({ip: entry.substring(3)});
        }
        else if (entry.toLowerCase().startsWith('email:')) {
            sanArray.push({rfc822: entry.substring(6)});
        }
        else {
            const dnsPrefixed = entry.toLowerCase().startsWith('dns:');
            if (isClient) {
                commonName = dnsPrefixed ? entry.substring(4) : entry;
            }
            else {
                const dns = dnsPrefixed ? entry.substring(4) : entry;
                sanArray.push({dns});
                if (sanArray.length === 1) {
                    commonName = dns; // Use first DNS as CN
                }
            }
        }
    }

    // STEP 2: create certificate signed by CA
    const extensions: Array<{
        extname: string;
        cA?: boolean;
        critical?: boolean;
        names?: string[];
        array?: (string[] | Array<{dns?: string, ip?: string, rfc822?: string}>);
    }> = [
        {extname: "basicConstraints", cA: false},
        {
            extname: "keyUsage",
            critical: true,
            names: ["digitalSignature", "keyEncipherment"]
        },
        {
            extname: "extKeyUsage",
            array: [isClient ? "clientAuth" : "serverAuth"]
        }
    ];

    // Add SAN only if we have entries
    if (sanArray.length > 0) {
        extensions.push({
            extname: "subjectAltName",
            array: sanArray
        });
    }

    const cert = new KJUR.asn1.x509.Certificate({
        version: 3,
        serial: { hex: randomSerialNumber() },
        issuer: { str: issuer },
        subject: { str: `/CN=${commonName}/O=${DEFAULT_ORGANISATION}/OU=${userAndHost}`},

        notbefore: datetozulu(new Date(Date.now() - 60 * 1000), false, false),
        notafter: datetozulu(new Date(Date.now() + validityDays * 24 * 60 * 60 * 1000), false, false),

        sbjpubkey: pub,
        ext: extensions,

        sigalg: "SHA256withECDSA",
        cakey: cakey
    });

    const certPem = cert.getPEM();

    return {
        key: prvPem,
        cert: certPem
    };
}
